import React, { useState, useCallback } from 'react';
import { leadsAPI } from '../../lib/api';
import { Button } from '../ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import { ListChecks, Loader2, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';

// One-click select an entire pipeline batch/source (e.g. a mistaken import) so it
// can be bulk-moved to Audience or deleted. Selection is capped at 500 server-side.
export default function BatchSelectMenu({ onSelect }) {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await leadsAPI.pipelineBatches();
      setBatches(data?.batches || []);
    } catch {
      setBatches([]);
    } finally { setLoading(false); }
  }, []);

  const pick = async (b) => {
    const key = `${b.field}:${b.value}`;
    setPicking(key);
    try {
      const { data } = await leadsAPI.selectBatch(b.field, b.value);
      const ids = data?.lead_ids || [];
      if (!ids.length) { toast.info('No selectable leads in that batch'); return; }
      onSelect(ids, `${b.value} · ${ids.length}`);
    } catch {
      toast.error('Could not select that batch');
    } finally { setPicking(null); }
  };

  return (
    <DropdownMenu onOpenChange={(o) => { if (o) load(); }}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 text-xs" data-testid="batch-select-trigger">
          <ListChecks className="h-3.5 w-3.5 mr-1.5" /> Select batch <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 max-h-80 overflow-auto" data-testid="batch-select-menu">
        <DropdownMenuLabel>Select an entire batch / source</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {loading && (
          <div className="flex items-center gap-2 px-2 py-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading batches…
          </div>
        )}
        {!loading && batches.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted-foreground">No pipeline batches found.</div>
        )}
        {!loading && batches.map((b) => {
          const key = `${b.field}:${b.value}`;
          return (
            <DropdownMenuItem
              key={key}
              onSelect={(e) => { e.preventDefault(); pick(b); }}
              className="flex items-center justify-between gap-3 cursor-pointer"
              data-testid={`batch-option-${b.field}-${b.value}`}
            >
              <span className="truncate">
                <span className="font-medium">{b.value}</span>
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">{b.field === 'source' ? 'source' : 'import'}</span>
              </span>
              <span className="flex items-center gap-1.5 shrink-0 text-xs text-muted-foreground tabular-nums">
                {picking === key ? <Loader2 className="h-3 w-3 animate-spin" /> : b.count}
              </span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
